// API configuration with dynamic local/remote mode
// Two modes:
//  - local:  http://localhost:5000
//  - remote: https://api.imprimetustarjetas.mx
// Precedence for base URL resolution (highest to lowest):
// 1) Explicit REACT_APP_API_BASE_URL (HIGHEST PRIORITY)
// 2) window.__API_BASE_URL (injected before app loads)
// 3) localStorage('API_MODE') => 'local' or 'remote'
// 4) Host detection: localhost/127.* => local, otherwise remote

const LOCAL_BASE = 'http://localhost:5001';
const REMOTE_BASE = 'https://printerserver.maticamexico.com.mx';

function detectDefaultMode() {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname || '';
    if (host === 'localhost' || host === '127.0.0.1') return 'local';
  }
  return 'remote';
}

export function getApiMode() {
  // 1. HIGHEST PRIORITY: Check explicit env variable FIRST
  if (import.meta.env && import.meta.env.VITE_API_BASE_URL) {
    const base = import.meta.env.VITE_API_BASE_URL;
    return base.includes('localhost') ? 'local' : 'custom';
  }
  // 2. Window override
  if (typeof window !== 'undefined' && window.__API_BASE_URL) return 'custom';
  // 3. LocalStorage
  const stored = (typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem('API_MODE')) || '';
  if (stored === 'local' || stored === 'remote') return stored;
  // 4. Auto-detect
  return detectDefaultMode();
}

export function setApiMode(mode) {
  if (typeof window === 'undefined') return;
  if (mode !== 'local' && mode !== 'remote') return;
  try { window.localStorage.setItem('API_MODE', mode); } catch { }
  const base = mode === 'local' ? LOCAL_BASE : REMOTE_BASE;
  window.__API_BASE_URL = base;
  if (window.console && console.log) {
    console.log(`?? API mode set to: ${mode} ? ${base}`);
  }
}

export function getApiBaseUrl() {
  // 1) Window override (highest priority)
  if (typeof window !== 'undefined' && window.__API_BASE_URL) {
    return String(window.__API_BASE_URL).replace(/\/$/, '');
  }

  // 2) Check if explicitly in development mode
  const isDev = import.meta.env && import.meta.env.DEV;

  // 3) Auto-detect based on current hostname
  if (typeof window !== 'undefined' && window.location) {
    const host = window.location.hostname;
    // ONLY use localhost if we're actually on localhost AND in dev mode
    if ((host === 'localhost' || host === '127.0.0.1') && isDev) {
      return LOCAL_BASE;
    }
    // ALWAYS use production server otherwise
    return REMOTE_BASE;
  }

  // 4) Fallback to production (ALWAYS)
  return REMOTE_BASE;
}

export function buildApiUrl(path) {
  const base = getApiBaseUrl();
  if (!path) return base;
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}

// Fetch helper with basic error mapping
export async function apiFetch(path, options = {}) {
  const url = buildApiUrl(path);
  const res = await fetch(url, options);
  let data = null;
  try { data = await res.json(); } catch (_) { }
  if (!res.ok) {
    const msg = (data && (data.msg || data.error)) || `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

// Expose quick toggle for QA/debugging
if (typeof window !== 'undefined') {
  window.__API = window.__API || {};
  window.__API.getMode = getApiMode;
  window.__API.setMode = setApiMode;
  window.__API.getBase = getApiBaseUrl;
  if (window.console && console.log) {
    console.log(`?? API mode: ${getApiMode()} ? ${getApiBaseUrl()}`);
  }
}

// Legacy compatibility: allow importing { API_BASE_URL } from this module
// Some components might still do: import { API_BASE_URL } from '../config/api'
export const API_BASE_URL = getApiBaseUrl();

export default {
  getApiMode,
  setApiMode,
  getApiBaseUrl,
  buildApiUrl,
  apiFetch,
  API_BASE_URL
};
